import { countVolume, formatKg } from '@cadencia/domain';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EscalaInput } from '../../components/EscalaInput.js';
import { Boton, Estado, ListaDatos, Tarjeta } from '../../components/ui.js';
import { useAppData } from '../../data/DataProvider.js';
import { refreshProgressionState } from '../../data/repositories/progressionRepo.js';
import { isReadaptationPhase, requireExercise } from '@cadencia/domain';
import { getSessionDetail } from '../../data/repositories/trainingRepo.js';
import {
  finishWorkout,
  getExerciseHistory,
  getWorkoutBySession,
  listPerformedSets,
} from '../../data/repositories/workoutRepo.js';
import type { PerformedSet, TrainingBlock, WorkoutLog } from '@cadencia/contracts';
import './entrenar.css';
import { mensajeDeError } from '../../lib/errores.js';

/**
 * Cierre de sesión (requisito §15.3): dos preguntas, en segundos.
 * Sin celebraciones ni comparativas gamificadas (requisito §48).
 */
export function SesionCierrePage(): ReactNode {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { ctx, ready, refreshPending } = useAppData();
  const navigate = useNavigate();

  const [log, setLog] = useState<WorkoutLog | null | undefined>(undefined);
  const [series, setSeries] = useState<PerformedSet[]>([]);
  // El bloque de la sesion: sin el no se sabe si esto es readaptacion, y la
  // estrategia inicial de cada ejercicio depende de eso (BUG-002).
  const [bloque, setBloque] = useState<TrainingBlock | null>(null);
  const [dificultad, setDificultad] = useState(3);
  const [calidad, setCalidad] = useState(3);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    void (async () => {
      const encontrado = (await getWorkoutBySession(sessionId)) ?? null;
      setLog(encontrado);
      if (encontrado) setSeries(await listPerformedSets(encontrado.id));
      setBloque((await getSessionDetail(sessionId))?.block ?? null);
    })();
  }, [sessionId]);

  if (!ready || log === undefined) return <Estado tipo="cargando" titulo="Cargando sesión" />;
  if (log === null || !ctx) {
    return <Estado tipo="error" titulo="No hay ninguna sesión que cerrar" />;
  }

  const volumen = series.reduce((total, set) => total + set.loadKg * set.reps, 0);

  /*
   * Las series efectivas las cuenta el DOMINIO, igual que `finishWorkout`.
   *
   * Aqui habia `series.length`, que es el numero de FILAS. No es ninguna
   * definicion de «serie efectiva»: un drop vale media y un rest-pause vale una
   * y media (`setTypeWeight`). Asi que esta pantalla decia 7 y la fila que se
   * guardaba un segundo despues, con `countVolume`, decia 7,5 — y el resumen de
   * la semana, que lee lo guardado, no coincidia con el resumen del cierre.
   *
   * No es que hubiera un numero mal: es que habia dos cuentas de lo mismo. La
   * unica forma de que no vuelvan a separarse es que solo haya una (NEW2-001).
   */
  const seriesEfectivas = countVolume(
    series.map((set) => ({ exerciseSlug: set.exerciseSlug, setType: set.setType })),
  ).effectiveSets;
  const duracionMin = Math.round(
    (Date.now() - Date.parse(log.startedAt)) / 60_000,
  );

  const terminar = (): void => {
    setGuardando(true);
    setError(null);
    void (async () => {
      try {
        await finishWorkout(ctx, log, { difficulty: dificultad, quality: calidad });

        // El estado de progresión es DERIVADO: se recalcula desde los hechos
        // recién registrados, no se va acumulando por el camino.
        const combinaciones = new Map<string, { slug: string; equipmentId: string | null }>();
        for (const set of series) {
          combinaciones.set(`${set.exerciseSlug}|${set.equipmentId ?? ''}`, {
            slug: set.exerciseSlug,
            equipmentId: set.equipmentId,
          });
        }

        for (const { slug, equipmentId } of combinaciones.values()) {
          const history = await getExerciseHistory(ctx.userId, slug, equipmentId);
          await refreshProgressionState(ctx, {
            exerciseSlug: slug,
            equipmentId,
            history,
            sessionId: log.sessionId,
            context: {
              exerciseSlug: slug,
              equipmentId,
              equipmentKind: requireExercise(slug).equipmentKind,
              smallestIncrementKg: null,
              currentLoadKg: null,
              // La fase real. Cableado a `true`, todo ejercicio estrenaba con
              // la estrategia de readaptacion aunque el bloque fuera de
              // hipertrofia (BUG-002).
              isReadaptation: bloque === null || isReadaptationPhase(bloque.phase),
            },
          });
        }

        await refreshPending();
        navigate('/entrenar');
      } catch (caught) {
        setError(mensajeDeError(caught));
        setGuardando(false);
      }
    })();
  };

  return (
    <>
      <h1>¿Cómo ha ido?</h1>
      <p className="campo__pista">Dos preguntas y listo.</p>

      <Tarjeta>
        <EscalaInput
          etiqueta="Dificultad global"
          valor={dificultad}
          onChange={setDificultad}
          extremoBajo="Muy fácil"
          extremoAlto="Muy dura"
        />
        <EscalaInput
          etiqueta="Calidad percibida"
          valor={calidad}
          onChange={setCalidad}
          extremoBajo="Mala"
          extremoAlto="Excelente"
        />
      </Tarjeta>

      <Tarjeta titulo="Resumen">
        <ListaDatos
          filas={[
            {
              clave: 'Series efectivas',
              // Coma decimal: 7,5, no 7.5. Los pesos son multiplos de 0,5, asi
              // que un decimal basta y nunca sale un 7,50.
              valor: seriesEfectivas.toLocaleString('es-ES', { maximumFractionDigits: 1 }),
            },
            { clave: 'Volumen total', valor: formatKg(Math.round(volumen)) },
            { clave: 'Duración', valor: `${duracionMin} min` },
          ]}
        />
      </Tarjeta>

      <Boton ancho="completo" onClick={terminar} disabled={guardando}>
        {guardando ? 'Guardando…' : 'Terminar'}
      </Boton>

      {error !== null && (
        <p className="campo__error" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
